import { useState, useEffect } from "react";
import { useTheme, DARK, LIGHT } from "../../components/Layout";
import { vendorsAPI } from "../../services/api";
import { Search, Eye, Trash2, Filter, ChevronDown, ChevronRight, Edit2, X, FileText } from "lucide-react";
import SearchableSelect from "../../components/SearchableSelect";
import VendorReport from "./VendorReport";

interface Props { onSelect: (id: number) => void; initialSearch?: string; }
const fmt = (n: any) => new Intl.NumberFormat("en-US").format(Math.abs(Number(n) || 0));

const CURRENCIES = ["USD", "AFN", "IRR", "TRY", "PKR", "EUR"];

function BalanceBadge({ val, currency, T }: { val: any; currency: string; T: any }) {
  const n = Number(val);
  const color = n < 0 ? "#ef4444" : n > 0 ? "#10b981" : "#64748b";
  const label = n < 0 ? "قرضدار" : n > 0 ? "طلبکار" : "تسویه";
  return (
    <div style={{ textAlign: "left" }}>
      <div style={{ fontSize: 13, fontWeight: 800, color }}>{n > 0 ? "+" : ""}{fmt(n)} {currency}</div>
      <div style={{ fontSize: 10, color, opacity: 0.8 }}>{label}</div>
    </div>
  );
}

interface RowProps {
  v: any; indent?: boolean; T: any; dark: boolean; expanded: Set<number>;
  subs: Record<number, any[]>; toggleExpand: (id: number) => void;
  onSelect: (id: number) => void; handleDelete: (id: number, name: string) => void;
  onEdit: (v: any) => void; onReport: (v: any) => void;
}
function Row({ v, indent=false, T, dark, expanded, subs, toggleExpand, onSelect, handleDelete, onEdit, onReport }: RowProps) {
  return (
    <div>
      <div
        style={{ display: "grid", gridTemplateColumns: "32px 80px 1fr 130px 130px 150px 110px", gap: 10, padding: `12px ${indent ? "32px" : "18px"} 12px 18px`, borderBottom: `1px solid ${T.cardBorder}`, alignItems: "center", cursor: "pointer", transition: "background 0.15s", background: indent ? (dark ? "rgba(255,255,255,0.01)" : "rgba(0,0,0,0.01)") : "" }}
        onMouseEnter={e => (e.currentTarget.style.background = dark ? "rgba(245,158,11,0.04)" : "rgba(245,158,11,0.03)")}
        onMouseLeave={e => (e.currentTarget.style.background = indent ? (dark ? "rgba(255,255,255,0.01)" : "rgba(0,0,0,0.01)") : "")}
        onClick={() => onSelect(v.id)}
      >
        <div onClick={e => { e.stopPropagation(); if (v.sub_count > 0) toggleExpand(v.id); }} style={{ display: "flex", alignItems: "center", justifyContent: "center", cursor: v.sub_count > 0 ? "pointer" : "default" }}>
          {v.sub_count > 0 ? (expanded.has(v.id) ? <ChevronDown size={14} color={T.subtext} /> : <ChevronRight size={14} color={T.subtext} />) : <span style={{ width: 14 }} />}
        </div>
        <span style={{ fontSize: 11, fontFamily: "monospace", color: "#f59e0b", fontWeight: 700 }}>{v.code}</span>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 9, background: indent ? "linear-gradient(135deg,#64748b,#475569)" : "linear-gradient(135deg,#f59e0b,#d97706)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "white", fontWeight: 700, flexShrink: 0 }}>
            {v.first_name?.[0]}{v.last_name?.[0] || ""}
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{v.first_name} {v.last_name}</div>
            <div style={{ fontSize: 10, color: T.subtext }}>
              {v.company_name && `${v.company_name} • `}
              {v.sub_count > 0 && `${v.sub_count} زیرمجموعه • `}
              {v.tx_count} معامله
            </div>
          </div>
        </div>
        <span style={{ fontSize: 12, color: T.text }}>{v.phone || "—"}</span>
        <span style={{ fontSize: 12, color: T.subtext }}>{v.city || "—"}</span>
        <BalanceBadge val={v.current_balance} currency={v.currency} T={T} />
        <div style={{ display: "flex", gap: 5 }} onClick={e => e.stopPropagation()}>
          <button onClick={() => onSelect(v.id)} title="مشاهده" style={{ width: 28, height: 28, borderRadius: 7, border: "none", cursor: "pointer", background: "rgba(245,158,11,0.12)", color: "#f59e0b", display: "flex", alignItems: "center", justifyContent: "center" }}><Eye size={12} /></button>
          <button onClick={() => onReport(v)} title="گزارش" style={{ width: 28, height: 28, borderRadius: 7, border: "none", cursor: "pointer", background: "rgba(16,185,129,0.12)", color: "#10b981", display: "flex", alignItems: "center", justifyContent: "center" }}><FileText size={12} /></button>
          <button onClick={() => onEdit(v)} title="ویرایش" style={{ width: 28, height: 28, borderRadius: 7, border: "none", cursor: "pointer", background: "rgba(59,130,246,0.12)", color: "#3b82f6", display: "flex", alignItems: "center", justifyContent: "center" }}><Edit2 size={12} /></button>
          <button onClick={() => handleDelete(v.id, `${v.first_name} ${v.last_name}`)} title="حذف" style={{ width: 28, height: 28, borderRadius: 7, border: "none", cursor: "pointer", background: "rgba(239,68,68,0.12)", color: "#ef4444", display: "flex", alignItems: "center", justifyContent: "center" }}><Trash2 size={12} /></button>
        </div>
      </div>
      {expanded.has(v.id) && (subs[v.id] || []).map((sub: any) => (
        <Row key={sub.id} v={sub} indent T={T} dark={dark} expanded={expanded} subs={subs} toggleExpand={toggleExpand} onSelect={onSelect} handleDelete={handleDelete} onEdit={onEdit} onReport={onReport} />
      ))}
    </div>
  );
}

export default function VendorList({ onSelect, initialSearch = "" }: Props) {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const [vendors, setVendors]   = useState<any[]>([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState(initialSearch);
  const [status, setStatus]     = useState("");
  const [toast, setToast]       = useState("");
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [subs, setSubs]         = useState<Record<number, any[]>>({});
  const [editItem, setEditItem] = useState<any>(null);
  const [saving, setSaving]     = useState(false);
  const [reportItem, setReportItem] = useState<any>(null);

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  const load = async () => {
    setLoading(true);
    try {
      const r = await vendorsAPI.list({ search, status, parent_only: !search });
      if (r.success) setVendors(r.vendors);
    } catch { showToast("❌ خطا در اتصال"); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [search, status]);
  useEffect(() => { setSearch(initialSearch); }, [initialSearch]);

  const toggleExpand = async (id: number) => {
    const next = new Set(expanded);
    if (next.has(id)) { next.delete(id); }
    else {
      next.add(id);
      if (!subs[id]) {
        const r = await vendorsAPI.list({ parent_id: id });
        if (r.success) setSubs(s => ({ ...s, [id]: r.vendors }));
      }
    }
    setExpanded(next);
  };

  const handleDelete = async (id: number, name: string) => {
    if (!window.confirm(`"${name}" حذف شود؟`)) return;
    const r = await vendorsAPI.delete(id);
    if (r.success) { showToast("✅ حذف شد"); load(); }
    else showToast("❌ " + r.message);
  };

  const handleEdit = (v: any) => {
    setEditItem({
      id: v.id,
      first_name: v.first_name || "",
      last_name: v.last_name || "",
      company_name: v.company_name || "",
      phone: v.phone || "",
      phone2: v.phone2 || "",
      address: v.address || "",
      city: v.city || "",
      country: v.country || "",
      currency: v.currency || "USD",
      opening_balance: String(Math.abs(v.opening_balance || 0)),
      opening_balance_type: v.opening_balance_type || "credit",
      notes: v.notes || "",
      status: v.status || "active",
    });
  };

  const handleSave = async () => {
    if (!editItem) return;
    setSaving(true);
    const payload = {
      ...editItem,
      opening_balance: parseFloat(editItem.opening_balance || "0"),
    };
    const r = await vendorsAPI.update(payload);
    setSaving(false);
    if (r.success) {
      showToast("✅ فروشنده ویرایش شد");
      setEditItem(null);
      load();
    } else showToast("❌ " + r.message);
  };

  const inp: React.CSSProperties = { padding: "9px 14px", background: dark ? "rgba(255,255,255,0.05)" : "#f8faff", border: `1.5px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`, borderRadius: 10, color: T.text, fontSize: 13, fontFamily: "'Vazirmatn','Segoe UI',sans-serif", direction: "rtl" as const, outline: "none", width: "100%", boxSizing: "border-box" as const };
  const statusOptions = [{ value: "active", label: "فعال" }, { value: "inactive", label: "غیرفعال" }];

  return (
    <div dir="rtl">
      {toast && <div style={{ position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", zIndex: 999, padding: "12px 24px", borderRadius: 12, background: dark ? "#1e293b" : "#fff", boxShadow: "0 8px 32px rgba(0,0,0,0.3)", color: T.text, fontSize: 13, fontWeight: 600, fontFamily: "inherit" }}>{toast}</div>}

      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ flex: 1, minWidth: 200, position: "relative" }}>
          <Search size={14} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: T.subtext }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="جستجو در نام، کد، شماره تماس..." style={{ ...inp, paddingRight: 36 }} />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Filter size={14} color={T.subtext} />
          <div style={{ width: 160 }}>
            <SearchableSelect options={statusOptions} value={status} onChange={v => setStatus(v)} placeholder="همه وضعیت‌ها"/>
          </div>
        </div>
      </div>

      <div style={{ borderRadius: 16, overflow: "hidden", border: `1px solid ${T.cardBorder}`, background: dark ? "rgba(255,255,255,0.02)" : "#fff" }}>
        <div style={{ display: "grid", gridTemplateColumns: "32px 80px 1fr 130px 130px 150px 110px", gap: 10, padding: "12px 18px", background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)", borderBottom: `1px solid ${T.cardBorder}`, fontSize: 11, fontWeight: 700, color: T.subtext }}>
          <span /><span>کد</span><span>فروشنده</span><span>تماس</span><span>شهر</span><span>بیلانس</span><span>عملیات</span>
        </div>
        {loading ? (
          <div style={{ textAlign: "center", padding: 60, color: T.subtext }}>
            <div style={{ width: 32, height: 32, border: "3px solid rgba(245,158,11,0.2)", borderTop: "3px solid #f59e0b", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 12px" }} />
            در حال بارگذاری...
          </div>
        ) : vendors.length === 0 ? (
          <div style={{ textAlign: "center", padding: 60, color: T.subtext }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>🏪</div>
            <div>هیچ فروشنده‌ای پیدا نشد</div>
          </div>
        ) : vendors.map(v => (
          <Row key={v.id} v={v} T={T} dark={dark} expanded={expanded} subs={subs} toggleExpand={toggleExpand} onSelect={onSelect} handleDelete={handleDelete} onEdit={handleEdit} onReport={setReportItem} />
        ))}
      </div>

      {/* Modal ویرایش */}
      {editItem && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}
          onClick={e => { if (e.target === e.currentTarget) setEditItem(null); }}>
          <div style={{ background: dark ? "#1e293b" : "#fff", borderRadius: 20, padding: 28, width: "100%", maxWidth: 560, maxHeight: "90vh", overflow: "auto", boxShadow: "0 25px 60px rgba(0,0,0,0.4)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: T.text }}>ویرایش فروشنده</h3>
              <button onClick={() => setEditItem(null)} style={{ background: "none", border: "none", cursor: "pointer", color: T.subtext }}><X size={20} /></button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: T.subtext, display: "block", marginBottom: 5 }}>نام</label>
                <input value={editItem.first_name} onChange={e => setEditItem((f: any) => ({ ...f, first_name: e.target.value }))} style={inp} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: T.subtext, display: "block", marginBottom: 5 }}>تخلص</label>
                <input value={editItem.last_name} onChange={e => setEditItem((f: any) => ({ ...f, last_name: e.target.value }))} style={inp} />
              </div>
              <div style={{ gridColumn: "1/-1" }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: T.subtext, display: "block", marginBottom: 5 }}>نام شرکت</label>
                <input value={editItem.company_name} onChange={e => setEditItem((f: any) => ({ ...f, company_name: e.target.value }))} style={inp} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: T.subtext, display: "block", marginBottom: 5 }}>تماس ۱</label>
                <input value={editItem.phone} onChange={e => setEditItem((f: any) => ({ ...f, phone: e.target.value }))} style={inp} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: T.subtext, display: "block", marginBottom: 5 }}>تماس ۲</label>
                <input value={editItem.phone2} onChange={e => setEditItem((f: any) => ({ ...f, phone2: e.target.value }))} style={inp} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: T.subtext, display: "block", marginBottom: 5 }}>شهر</label>
                <input value={editItem.city} onChange={e => setEditItem((f: any) => ({ ...f, city: e.target.value }))} style={inp} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: T.subtext, display: "block", marginBottom: 5 }}>کشور</label>
                <input value={editItem.country} onChange={e => setEditItem((f: any) => ({ ...f, country: e.target.value }))} style={inp} />
              </div>
              <div style={{ gridColumn: "1/-1" }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: T.subtext, display: "block", marginBottom: 5 }}>آدرس</label>
                <input value={editItem.address} onChange={e => setEditItem((f: any) => ({ ...f, address: e.target.value }))} style={inp} />
              </div>
              {/* بیلانس اولیه */}
              <div style={{ gridColumn: "1/-1", background: dark ? "rgba(245,158,11,0.08)" : "#fffbeb", borderRadius: 12, padding: "14px 16px", border: "1.5px solid #fcd34d" }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: "#f59e0b", marginBottom: 12 }}>💰 بیلانس اولیه</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 700, color: T.subtext, display: "block", marginBottom: 5 }}>مبلغ</label>
                    <input type="number" value={editItem.opening_balance} onChange={e => setEditItem((f: any) => ({ ...f, opening_balance: e.target.value }))} style={inp} placeholder="0.00" />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 700, color: T.subtext, display: "block", marginBottom: 5 }}>نوع</label>
                    <select value={editItem.opening_balance_type} onChange={e => setEditItem((f: any) => ({ ...f, opening_balance_type: e.target.value }))} style={{ ...inp }}>
                      <option value="credit">بستانکار (طلبکار)</option>
                      <option value="debit">بدهکار (قرضدار)</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 700, color: T.subtext, display: "block", marginBottom: 5 }}>ارز</label>
                    <select value={editItem.currency} onChange={e => setEditItem((f: any) => ({ ...f, currency: e.target.value }))} style={{ ...inp }}>
                      {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: T.subtext, display: "block", marginBottom: 5 }}>وضعیت</label>
                <select value={editItem.status} onChange={e => setEditItem((f: any) => ({ ...f, status: e.target.value }))} style={{ ...inp }}>
                  <option value="active">فعال</option>
                  <option value="inactive">غیرفعال</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: T.subtext, display: "block", marginBottom: 5 }}>یادداشت</label>
                <input value={editItem.notes} onChange={e => setEditItem((f: any) => ({ ...f, notes: e.target.value }))} style={inp} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
              <button onClick={() => setEditItem(null)} style={{ padding: "10px 20px", borderRadius: 10, border: `1.5px solid ${T.cardBorder}`, background: "transparent", color: T.text, cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>انصراف</button>
              <button onClick={handleSave} disabled={saving} style={{ padding: "10px 24px", borderRadius: 10, border: "none", background: saving ? "#9ca3af" : "linear-gradient(135deg,#f59e0b,#d97706)", color: "#fff", cursor: saving ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700, fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>
                {saving ? "در حال ذخیره..." : "ذخیره ویرایش"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Modal گزارش */}
      {reportItem && (
        <VendorReport
          entity={reportItem}
          entityType="vendor"
          onClose={() => setReportItem(null)}
          T={T}
          dark={dark}
        />
      )}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}